import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Button, Divider, Flex, Text } from 'core/components';
import Page from 'app/components/page/Page';
import storeLoader from 'app/stores/storeLoader';
import { BiHistory } from 'react-icons/bi';
import { TbLayoutSidebarRightCollapse } from 'react-icons/tb';
import KaizenMainContent from './KaizenMainContent';

@storeLoader('$users')
@inject('$auth', '$companySettings', '$journeys')
@withRouter
@observer
class KaizenMain extends Component {
  state = {
    isMyJourneysOpen: true,
    isCompanyJourneysOpen: true,
    isScrolledToBottom: false,
    isSidebarCollapsed: true,
    loading: true,
    selectedToolResults: [],
    isExistingChatLoading: false,
    searchInputValue: ''
  };

  inputRef = React.createRef();

  scrollAreaRef = React.createRef();

  componentDidMount() {
    const { history, match, $journeys } = this.props;
    const { sessionId } = match.params;

    if (this.hasAi) {
      $journeys.chatSessionCollection.fetch().then(() => {
        this.setState({ loading: false }, () => {
          if ($journeys.chatSessionCollection.size > 0) {
            if (sessionId) {
              const journey = $journeys.chatSessionCollection.get(sessionId);
              if (journey) {
                this.handleSelectJourney(journey);
              } else {
                // journey not found
                history.push($journeys.baseUrl);
              }
            }
          }
        });
      });
    }
  }

  handleEmptyStart = () => {
    const { history, $journeys } = this.props;
    $journeys.chatSessionCollection?.unselectAll();
    this.setState({
      selectedToolResults: [],
      isExistingChatLoading: false
    });
    history.push($journeys.baseUrl);
  };

  componentWillUnmount() {
    const { $journeys } = this.props;

    // Remove window resize listener
    window.removeEventListener('resize', this.handleResize);

    $journeys.chatSessionCollection.models.forEach((model) => model.unsubscribe());
    $journeys.chatSessionCollection.clearFilters();
    $journeys.chatSessionCollection.clearSelection();
  }

  componentDidUpdate(prevProps) {
    const { history, match, $journeys } = this.props;
    const { match: prevMatch } = prevProps;

    if (match.params.sessionId && match.params.sessionId !== prevMatch.params.sessionId) {
      const journey = $journeys.chatSessionCollection.get(match.params.sessionId);
      if (journey) {
        this.handleSelectJourney(journey);
      } else {
        // journey not found
        history.push($journeys.baseUrl);
      }
    }
  }

  handleScroll = () => {
    const { scrollHeight, scrollTop } = this.scrollAreaRef.current;
    const scrollAreaHeight = this.scrollAreaRef.current.clientHeight;

    //  Math.abs(... <= 1) → Checks if the difference is very small (≤ 1px) to account for rounding errors in different browsers.
    const isBottom = Math.abs(scrollHeight - Math.ceil(scrollTop) - scrollAreaHeight) <= 1;
    this.setState({ isScrolledToBottom: isBottom });
  };

  scrollToBottom = () => {
    if (this.scrollAreaRef?.current) {
      setTimeout(() => {
        const offsetBottom = this.scrollAreaRef.current.scrollTop + this.scrollAreaRef.current.scrollHeight + 100;
        this.scrollAreaRef.current.scrollTo({ top: offsetBottom, behavior: 'smooth' });
      }, 350);
    }
  };

  handleCreateJourney = () => {
    const { $journeys } = this.props;
    const newJourney = $journeys.createJourney();
    this.setState({ isExistingChatLoading: false });
    return newJourney;
  };

  handleSelectJourneyAfterCreation = (journey) => {
    const { history, $journeys } = this.props;
    history.push(`${$journeys.baseUrl}/${journey.id}`);
  };

  handleToggleMyJourneys = () => {
    this.setState((prevState) => {
      const { isMyJourneysOpen } = prevState;
      return { isMyJourneysOpen: !isMyJourneysOpen };
    });
  };

  handleToggleCompanyJourneys = () => {
    this.setState((prevState) => {
      const { isCompanyJourneysOpen } = prevState;
      return { isCompanyJourneysOpen: !isCompanyJourneysOpen };
    });
  };

  handleSearchInputChange = (e) => {
    const { $journeys } = this.props;
    const { value = '' } = e.target;
    this.setState({ searchInputValue: value });
    $journeys.chatSessionCollection.searchChats(value);
  };

  handleSelectJourney = (journey) => {
    const { history, $journeys } = this.props;
    this.setState({
      selectedToolResults: [],
      isExistingChatLoading: true
    });
    history.push(`${$journeys.baseUrl}/${journey.id}`);
    journey.selectAndSubscribe({ fetch: true });
  };

  handleCloseToolResults = () => {
    this.setState({ selectedToolResults: [] });
  };

  handleSidebarCollapse = () => {
    const { isSidebarCollapsed } = this.state;
    this.setState({ isSidebarCollapsed: !isSidebarCollapsed });
  };

  handleViewPrompt() {
    window.open('/api/ui/kaizen/context', '_blank');
  }

  handleCloseToolResult = (tool) => {
    this.setState((prevState) => {
      const selectedToolResults = prevState.selectedToolResults.filter((t) => t.id !== tool.id);
      return { selectedToolResults };
    });
  };

  handleSelectToolResult = (tool) => {
    this.setState((prevState) => {
      const toolExists = prevState.selectedToolResults.some((existingTool) => existingTool.id === tool.id);
      const updates = {};
      if (!toolExists) {
        updates.selectedToolResults = [...prevState.selectedToolResults, tool];
      }
      if (!prevState.isSidebarCollapsed) {
        updates.isSidebarCollapsed = true;
      }
      return Object.keys(updates).length > 0 ? updates : null;
    });
  };

  get hasAi() {
    const { $companySettings, $auth } = this.props;
    return !!$companySettings.enableKentikAi && $auth.hasPermission('kaizen', { overrideForSudo: false });
  }

  renderSubnavTools = () => {
    const { $companySettings } = this.props;
    const { selectedToolResults, isCreatingJourney, isSidebarCollapsed } = this.state;

    if (!this.hasAi) {
      return null;
    }

    return (
      <Flex>
        {$companySettings.demobox.enabled && (
          <>
            <Button minimal icon="search-template" text="Start Demo" onClick={this.handleStartInvestigation} mr={1} />
          </>
        )}
        {selectedToolResults?.length > 0 && (
          <>
            <Button
              icon={TbLayoutSidebarRightCollapse}
              text="Close Results"
              minimal
              onClick={this.handleCloseToolResults}
            />
            <Divider mx={1} />
          </>
        )}
        <Button minimal onClick={this.handleEmptyStart} loading={isCreatingJourney} icon="plus">
          <Text>New</Text>
        </Button>

        <Button
          minimal
          icon={BiHistory}
          text="History"
          active={!isSidebarCollapsed}
          onClick={this.handleSidebarCollapse}
          ml={1}
        />
      </Flex>
    );
  };

  render() {
    const { $journeys, loading: loadingStores } = this.props;
    const {
      loading,
      selectedToolResults,
      isSidebarCollapsed,
      isScrolledToBottom,
      isExistingChatLoading,
      searchInputValue
    } = this.state;

    const selectedJourney = $journeys.chatSessionCollection?.selected;

    let title = 'New Conversation';
    if (selectedJourney) {
      title = `${selectedJourney.get('session_title')}`;
    }

    return (
      <Page pt={0} pb={0} px={0} title={title} scrolls={!this.hasAi} subnavTools={this.renderSubnavTools()}>
        <KaizenMainContent
          hasAi={this.hasAi}
          loading={loading}
          loadingStores={loadingStores}
          isScrolledToBottom={isScrolledToBottom}
          isExistingChatLoading={isExistingChatLoading}
          isSidebarCollapsed={isSidebarCollapsed}
          inputRef={this.inputRef}
          scrollAreaRef={this.scrollAreaRef}
          selectedToolResults={selectedToolResults}
          searchInputValue={searchInputValue}
          onScroll={this.handleScroll}
          onScrollToBottom={this.scrollToBottom}
          onSelectToolResult={this.handleSelectToolResult}
          onCreateJourney={this.handleCreateJourney}
          onSelectJourneyAfterCreation={this.handleSelectJourneyAfterCreation}
          onCloseToolResult={this.handleCloseToolResult}
          onCloseToolResults={this.handleCloseToolResults}
          onSelectJourney={this.handleSelectJourney}
          onToggleMyJourneys={this.handleToggleMyJourneys}
          onToggleCompanyJourneys={this.handleToggleCompanyJourneys}
          onSearchInputChange={this.handleSearchInputChange}
          onViewPrompt={this.handleViewPrompt}
        />
      </Page>
    );
  }
}

export default KaizenMain;
